import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Chip
} from '@mui/material';
import { ArrowBack, AutoAwesome, Lock, Sync, CreditCard, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { updatePassword } from 'aws-amplify/auth';
import { get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

function Settings({ signOut, user }) {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const makeAuthenticatedRequest = async (path: string, options: any = {}) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (options.method === 'POST') {
        return await post({
          apiName: 'reviewReplyApi',
          path: path,
          options: {
            ...options,
            headers: {
              'Authorization': `Bearer ${token}`,
              ...options.headers
            }
          }
        }).response;
      } else {
        return await get({
          apiName: 'reviewReplyApi',
          path: path,
          options: {
            ...options,
            headers: {
              'Authorization': `Bearer ${token}`,
              ...options.headers
            }
          }
        }).response;
      }
    } catch (error) {
      console.error('Auth request failed:', error);
      throw error;
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const response: any = await makeAuthenticatedRequest('/subscription');
      const data: any = await response.body.json();
      setSubscriptionStatus(data);
    } catch (err) {
      console.error('Error loading subscription status:', err);
    }
  };

  const handleChangePlan = async () => {
    try {
      const currentPlan = subscriptionStatus.plan_type;
      const newPlan = currentPlan === 'basic' ? 'pro' : 'basic';
      
      const response: any = await makeAuthenticatedRequest('/stripe/change-plan', {
        method: 'POST',
        body: JSON.stringify({ plan_type: newPlan }),
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data: any = await response.body.json();
      console.log('Plan changed:', data);
      
      // Reload subscription status
      loadSubscriptionStatus();
      setSuccess(`Plan changed to ${newPlan.toUpperCase()} successfully!`);
      
    } catch (err) {
      console.error('Error changing plan:', err);
      setError('Failed to change plan. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response: any = await makeAuthenticatedRequest('/stripe/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data: any = await response.body.json();
      console.log('Subscription cancelled:', data);
      
      // Reload subscription status
      loadSubscriptionStatus();
      setSuccess('Subscription cancelled successfully.');
      
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('Failed to cancel subscription. Please try again.');
    }
  };

  const handleResubscribe = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const currentPlan = subscriptionStatus?.plan_type || 'basic';
      
      const response: any = await makeAuthenticatedRequest('/stripe/resubscribe', {
        method: 'POST',
        body: JSON.stringify({ plan_type: currentPlan }),
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data: any = await response.body.json();
      console.log('Resubscribed:', data);
      
      // Reload subscription status
      await loadSubscriptionStatus();
      setSuccess('Subscription reactivated successfully!');
      
    } catch (err) {
      console.error('Error resubscribing:', err);
      setError('Failed to reactivate subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncLocations = async () => {
    setSyncLoading(true);
    setError('');
    setSuccess('');

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await get({
        apiName: 'reviewReplyApi',
        path: '/businesses?refresh=true',
        options: {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      }).response;

      const data: any = await response.body.json();
      setSuccess(`Successfully synced ${data.locations?.length || 0} locations from Google Business Profile`);
    } catch (error) {
      console.error('Sync failed:', error);
      setError('Failed to sync locations. Please try again.');
    } finally {
      setSyncLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword
      });

      setSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Password update failed:', error);
      setError(error.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Button 
            color="inherit" 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/app')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mr: 2 }}>
            ReplyMojo
          </Typography>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Settings
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.attributes?.email || user?.signInDetails?.loginId || user?.username}
          </Typography>
          <Button color="inherit" onClick={signOut}>
            Sign Out
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Lock sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h5">Account Settings</Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" gutterBottom>
              Google Business Profile
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sync your latest locations from Google Business Profile
            </Typography>

            <Button
              variant="outlined"
              startIcon={syncLoading ? <CircularProgress size={20} /> : <Sync />}
              onClick={handleSyncLocations}
              disabled={syncLoading}
              sx={{ mb: 4 }}
            >
              {syncLoading ? 'Syncing...' : 'Sync Locations'}
            </Button>

            <Divider sx={{ mb: 3 }} />

            {/* Subscription Management Section */}
            <Typography variant="h6" gutterBottom>
              Subscription Management
            </Typography>
            
            {subscriptionStatus ? (
              <Box sx={{ mb: 4 }}>
                {subscriptionStatus.has_subscription ? (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Current Plan: <Chip 
                            label={subscriptionStatus.plan_type?.toUpperCase()} 
                            color="primary" 
                            size="small" 
                          />
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status: {subscriptionStatus.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Locations: {subscriptionStatus.locations_limit} | 
                          Responses: {subscriptionStatus.responses_limit}/month
                        </Typography>
                        {subscriptionStatus.status === 'trial' && (
                          <Typography variant="body2" color="warning.main">
                            Trial ends in {subscriptionStatus.days_remaining} days
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {subscriptionStatus.status !== 'cancelled' ? (
                          <>
                            <Button 
                              variant="outlined" 
                              size="small"
                              startIcon={<CreditCard />}
                              onClick={handleChangePlan}
                            >
                              Switch to {subscriptionStatus.plan_type === 'basic' ? 'Pro' : 'Basic'}
                            </Button>
                            
                            <Button 
                              variant="outlined" 
                              color="error"
                              size="small"
                              startIcon={<Cancel />}
                              onClick={handleCancelSubscription}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button 
                            variant="contained" 
                            color="primary"
                            size="small"
                            startIcon={<CreditCard />}
                            onClick={handleResubscribe}
                            disabled={loading}
                          >
                            Resubscribe
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No active subscription. Visit the main dashboard to start your free trial.
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Loading subscription status...
              </Typography>
            )}

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Update your account password to keep your account secure
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordReset}>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading}
              />

              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading}
                helperText="Password must be at least 8 characters long"
              />

              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading}
              />

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                  startIcon={loading ? <CircularProgress size={20} /> : <Lock />}
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => navigate('/app')}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default Settings;
